import React, { useContext, useEffect, useState } from "react";
import { orgManagesUsers, personHasCalculator } from "helpers/calculatorAccess";
import { NavWrapper, NavInner, NavInnerMenu, LogoWrapper, LinkWrapper, NavSubtitle, NavToggle, ToggleButton } from "./styled";
import UserContext from "context/User";
import CompanyIcon from "components/svg/icons/CompanyIcon";
import { CreditCardIcon } from "components/svg";
import SettingsIcon from "components/svg/icons/SettingsIcon";
import AdminLogo from "components/img/AdminLogo";
import NavLink from "components/ui/NavLink";
import NavLinkGroup from "components/ui/NavLinkGroup";
import DashboardIcon from "components/svg/icons/DashboardIcon";
import ClientsIcon from "components/svg/icons/ClientsIcon";
import LeadsIcon from "components/svg/icons/LeadsIcon";
import OpportunitiesIcon from "components/svg/icons/OpportunitiesIcon";
import ProposalsIcon from "components/svg/icons/ProposalsIcon";
import MaintenanceIcon from "components/svg/icons/MaintenanceIcon";
import WorkOrdersIcon from "components/svg/icons/WorkOrdersIcon";
import LocationsIcon from "components/svg/icons/LocationsIcon";
import CalculatorIcon from "components/svg/icons/CalculatorIcon";
import { GroupsIcon, SupportIcon, ProvidersIcon, RegionsIcon, RolesIcon, ServiceAreaIcon, ServicesIcon, UsersIcon, ProjectsIcon, InvoicesIcon, PurchaseOrderIcon, ChatIcon } from "components/svg";
import { useLocation, useNavigate } from "react-router-dom";
import ProductsIcon from "components/svg/icons/ProductsIcon";
import RFQIcon from "components/svg/icons/RFQIcon";
import QuotesIcon from "components/svg/icons/QuotesIcon";
import CalendarIcon from "components/svg/icons/CalendarIcon";
import ReportsIcon from "components/svg/icons/ReportsIcon";
import { getPendingProviderInvoices, getMyProviderInvoices } from "api/ProviderInvoice";
import { getExpiringItems } from "api/QuoteExpiration";
import { getWorkOrderCounts } from "api/ServicePlans";
import { getLeadSettings } from "api/Leads";
import { getMoSettings } from "api/MaintenanceOpportunities";

const NavbarLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, setUser } = useContext<any>(UserContext);
  const [navToggle, setNavbarToggle] = useState<boolean>(false);

  // N1 - the portal-Locations capability lives on the client ORGANIZATION
  // (the type-2 Company client). A user under that org has `user.client`
  // = their own record with the org as `.parent`, so we read the flag
  // parent-aware (mirrors the backend's `parent ?? self` resolution). An
  // individual customer with no parent reads the flag off their own
  // client. The auth session now loads `client.parent` so the parent's
  // flag is present here.
  const portalLocationsEnabled =
    user?.client?.parent?.portalLocationsEnabled === true ||
    (user?.client && !user?.client?.parent
      ? user?.client?.portalLocationsEnabled === true
      : false);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState<number | null>(null);
  const [expiringQuoteCount, setExpiringQuoteCount] = useState<number | null>(null);
  const [expiringRfqCount, setExpiringRfqCount] = useState<number | null>(null);
  const [unmatchedWoCount, setUnmatchedWoCount] = useState<number | null>(null);
  // Leads module nav gate: the company setting LEADS_MODULE_ENABLED, read
  // once per session. Off = the link does not exist; the routes 403 anyway.
  const [leadsEnabled, setLeadsEnabled] = useState<boolean>(false);
  // Maintenance opportunities nav gate: leads on AND the MO switch on AND
  // the maintenanceOppView permission. Off = the link does not exist.
  const [moEnabled, setMoEnabled] = useState<boolean>(false);
  useEffect(() => {
    const load = () => {
      if (leadsEnabled && (user?.role?.permissions || []).includes("maintenanceOppView")) {
        getMoSettings().then((s: any) => setMoEnabled(!s?.error && !!s?.settings?.enabled));
      } else setMoEnabled(false);
    };
    load();
    // Settings > Leads > Maintenance opportunities fires this on Save, so the
    // link appears / disappears without a page reload.
    window.addEventListener("mo-settings-changed", load);
    return () => window.removeEventListener("mo-settings-changed", load);
  }, [leadsEnabled, user?.id]); // eslint-disable-line
  useEffect(() => {
    if (user?.accessLevel === "isCompany" && (user?.role?.permissions || []).includes("leadView")) {
      getLeadSettings().then((s) => setLeadsEnabled(!!s?.enabled));
    }
  }, [user?.id, user?.accessLevel]); // eslint-disable-line
  // Session 66 / PERFORMANCE_COST_AUDIT finding #1 - client Maintenance
  // nav visibility. The client-side Maintenance link (below) shows only
  // when the client actually has >=1 maintenance plan. This is now read
  // straight off the session user payload (`hasMaintenancePlan`, stamped
  // by the auth verify/login path via one indexed EXISTS probe), so the
  // navbar no longer fires its own `GET /maintenance/plans` list read on
  // every client load. Undefined for non-client roles (they don't render
  // the link anyway).
  const hasMaintenancePlan = user?.hasMaintenancePlan === true;

  const toggleNavBar = () => setNavbarToggle(!navToggle);

  useEffect(() => {
    document.body.className= navToggle ? "navbar-open" : "";
  },[navToggle]);  
  


  useEffect(() => {
    const handleCustomEvent = (e: any) => {
      setNavbarToggle((old) => !old);
    };
    document.body.addEventListener("navBarToggle", handleCustomEvent);
    return () => { document.body.removeEventListener("navBarToggle", handleCustomEvent); };
  }, []);
  
  useEffect(() => {
    setNavbarToggle(false);
  },[location.pathname])

  // Fetch pending/rejected invoice count for badge
  useEffect(() => {
    if (user?.accessLevel === "isCompany") {
      const fetchCount = async () => {
        try {
          const res = await getPendingProviderInvoices();
          if (res.success && Array.isArray(res.data)) {
            setPendingInvoiceCount(res.data.length > 0 ? res.data.length : null);
          }
        } catch (e: any) {
          // Silently fail — badge just won't show
        }
      };
      fetchCount();
      // Refresh every 60 seconds
      const interval = setInterval(fetchCount, 60000);
      return () => clearInterval(interval);
    }
    if (user?.accessLevel === "isProvider") {
      const fetchCount = async () => {
        try {
          // Fetch provider invoices for rejected count
          const invoiceRes = await getMyProviderInvoices();

          let actionCount = 0;

          // Count rejected invoices needing resubmission
          if (invoiceRes.success && Array.isArray(invoiceRes.data)) {
            const rejected = invoiceRes.data.filter((inv: any) => {
              if (inv.status !== "rejected") return false;
              const poStatus = Number(inv.purchaseOrder?.status ?? 0);
              const poPaymentStatus = Number(inv.purchaseOrder?.paymentStatus ?? 0);
              if (poStatus === 5 || poPaymentStatus === 2) return false;
              const hasNewerSubmission = invoiceRes.data.some(
                (other: any) =>
                  other.id !== inv.id &&
                  other.purchaseOrder?.id === inv.purchaseOrder?.id &&
                  !['rejected', 'superseded', 'duplicate_ignored'].includes(other.status) &&
                  new Date(other.createdAt) > new Date(inv.createdAt)
              );
              return !hasNewerSubmission;
            });
            actionCount += rejected.length;
          }

          setPendingInvoiceCount(actionCount > 0 ? actionCount : null);
        } catch (e: any) {
          // Silently fail
        }
      };
      fetchCount();
      const interval = setInterval(fetchCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.accessLevel]);

  // Fetch expiring quote/RFQ counts for nav badges.
  //
  // PERFORMANCE_COST_AUDIT finding #2 (Session 67): the cadence is split
  // by role because the data model is different for each, not to give
  // clients a lesser version of the same thing.
  //   - STAFF (isCompany): a live work queue. The operator wants the
  //     RFQ/Quote expiry badges to move as their own actions and the
  //     expiration job land, so this keeps the 60s poll.
  //   - CLIENT (isClient/isClientAdmin): a status glance. A client's own
  //     expiring count only changes when the scheduled backend
  //     expiration job flips an item across a multi-day window - there
  //     is nothing a client does in-session that moves it. Polling every
  //     60s re-fetched a number that structurally cannot change on that
  //     timescale (~1,440 Lambda calls/day per open client tab). So the
  //     client path fetches ONCE on load and stops; a client sees any
  //     change on their next navigation or reload, which is when they'd
  //     act on it anyway. Deliberately not stamped on the session
  //     payload (like hasMaintenancePlan): counts drift, so a login-time
  //     value would go stale - a light per-load fetch is the right
  //     transport, just not a repeating one.
  useEffect(() => {
    const isStaff = user?.accessLevel === "isCompany";
    const isClientViewer = ["isClient", "isClientAdmin"].includes(user?.accessLevel);
    if (!isStaff && !isClientViewer) return;

    const fetchExpiring = async () => {
      try {
        const data = await getExpiringItems();
        const qCount = Array.isArray(data?.quotes) ? data.quotes.length : 0;
        const rCount = Array.isArray(data?.rfqs) ? data.rfqs.length : 0;
        setExpiringQuoteCount(qCount > 0 ? qCount : null);
        setExpiringRfqCount(rCount > 0 ? rCount : null);
      } catch (e: any) {
        // Silently fail — badges just won't show
      }
    };
    fetchExpiring();

    // Staff keep the live 60s poll; clients fetch once and stop.
    if (isStaff) {
      const interval = setInterval(fetchExpiring, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.accessLevel]);

  // Fetch the Maintenance nav badge count.
  // §3.9 #16 — mirrors the pendingInvoiceCount polling pattern above.
  //
  // Session 80: this now reads the SERVER-SIDE AGGREGATE rather than
  // pulling every unmatched work order back just to call `.length` on
  // it — the unbounded read the counts endpoint was added to remove, on
  // a 60-second timer.
  //
  // The badge is "things in Maintenance waiting on a human", so it
  // carries BOTH unmatched work orders and outstanding client
  // cancellation requests. A cancellation request is the one item here
  // where a named customer is waiting for an answer, and it had no
  // presence in the nav at all — you had to already be on the queue to
  // learn one existed. The two are separated again on the queue's own
  // stat cards, which is where you act on them.
  useEffect(() => {
    if (user?.accessLevel === "isCompany") {
      const fetchMaintenanceCount = async () => {
        try {
          const res: any = await getWorkOrderCounts();
          const total =
            Number(res?.unmatched ?? 0) + Number(res?.cancellationRequests ?? 0);
          setUnmatchedWoCount(total > 0 ? total : null);
        } catch (e: any) {
          // Silently fail — badge just won't show
        }
      };
      fetchMaintenanceCount();
      const interval = setInterval(fetchMaintenanceCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.accessLevel]);

  return (
    <NavWrapper>      
      <NavInner>        
        <LogoWrapper>      
          <AdminLogo alt="" customStyles={{width: "auto!important"}} />                      
          <NavToggle><ToggleButton type="button" onClick={() => setNavbarToggle(!navToggle)} /></NavToggle>      
        </LogoWrapper>
        
        {user?.id && 
        <>
          {user?.accessLevel === "isSuper" && <>
            <NavInnerMenu> 
              <NavSubtitle>SETTINGS</NavSubtitle>           
              <LinkWrapper>
                <NavLink link={"/companies"} icon={CompanyIcon} label={"Companies"}></NavLink>
              </LinkWrapper>        
              <LinkWrapper>
                <NavLink link={"/supers"} icon={UsersIcon} label={"Users"}></NavLink>
              </LinkWrapper>
              <LinkWrapper>
                <NavLink link={"/expiration-settings"} icon={QuotesIcon} label={"Expiration"}></NavLink>
              </LinkWrapper>      
            </NavInnerMenu>
          </>}
          {user?.accessLevel === "isCompany" && <>
            <NavInnerMenu> 
              <NavSubtitle>GENERAL</NavSubtitle>
              <LinkWrapper>
                <NavLink link={"/dashboard"} icon={DashboardIcon} label={"Dashboard"}></NavLink>
              </LinkWrapper>  
              {/* Leads sit ABOVE Clients: top of the funnel (Jack). Same
                  table, different stage. Gated on the leadView permission
                  AND the company flag. */}
              {leadsEnabled && (
                <LinkWrapper>
                  <NavLink link={"/leads"} exclude={["/leads/opportunities"]} icon={LeadsIcon} label={"Leads"}></NavLink>
                </LinkWrapper>
              )}
              {moEnabled && (
                <LinkWrapper>
                  <NavLink link={"/leads/opportunities"} icon={OpportunitiesIcon} label={"Opportunities"}></NavLink>
                </LinkWrapper>
              )}
              <LinkWrapper>
                <NavLink link={"/clients"} icon={ClientsIcon} label={"Clients"}></NavLink>
              </LinkWrapper>  
              <LinkWrapper>
                <NavLink link={"/providers"} icon={ProvidersIcon} label={"Providers"}></NavLink>
              </LinkWrapper> 
              <LinkWrapper>
                <NavLink link={"/products"} icon={ProductsIcon} label={"Products"}></NavLink>
              </LinkWrapper> 
              <LinkWrapper>
                <NavLink link={"/services"} icon={ServicesIcon} label={"Services"}></NavLink>
              </LinkWrapper>              
              <LinkWrapper>
                <NavLink link={"/service-areas"} icon={ServiceAreaIcon} label={"Service Areas"}></NavLink>
              </LinkWrapper>
              <LinkWrapper>
                <NavLink link={"/quote-requests"} icon={RFQIcon} label={"RFQ's"} badge={expiringRfqCount}></NavLink>
              </LinkWrapper>   
              <LinkWrapper>
                <NavLink link={"/quotes"} icon={QuotesIcon} label={"Quotes"} badge={expiringQuoteCount}></NavLink>
              </LinkWrapper> 
              <LinkWrapper>
                <NavLink link={"/invoices"} icon={InvoicesIcon} label={"Invoices"}></NavLink>
              </LinkWrapper>  
               <LinkWrapper>
                <NavLink link={"/purchase-orders"} icon={PurchaseOrderIcon} label={"PO's"} badge={pendingInvoiceCount}></NavLink>
              </LinkWrapper>    
              <LinkWrapper>
                <NavLinkGroup
                  link={"/maintenance"}
                  icon={MaintenanceIcon}
                  label={"Maintenance"}
                  badge={unmatchedWoCount}
                  childPaths={["/maintenance"]}
                >
                  <NavLink link={"/maintenance/work-orders"} icon={WorkOrdersIcon} label={"Work Orders"} badge={unmatchedWoCount}></NavLink>
                </NavLinkGroup>
              </LinkWrapper>
              <LinkWrapper>
                <NavLink link={"/projects"} icon={ProjectsIcon} label={"Projects"}></NavLink>
              </LinkWrapper>
              <LinkWrapper>
                <NavLink link={"/locations"} icon={LocationsIcon} label={"Locations"}></NavLink>
              </LinkWrapper>
              <LinkWrapper>
                <NavLink link={"/calendar"} icon={CalendarIcon} label={"Calendar"}></NavLink>
              </LinkWrapper>
              <LinkWrapper>
                <NavLink link={"/reports"} icon={ReportsIcon} label={"Reports"}></NavLink>
              </LinkWrapper>

              <NavSubtitle $mt={"20px"}>TOOLS</NavSubtitle>
              <LinkWrapper>
                <NavLink link={"/mulch-calculator"} icon={CalculatorIcon} label={"Mulch Calculator"}></NavLink>
              </LinkWrapper>
              {/* Proposal generator — COMPANY NAV ONLY. Deliberately not
                  mirrored into the client TOOLS block below: a proposal
                  record carries provider cost, margin and prospect data.
                  Build spec §11.

                  Session 76: gated on proposalView, matching the userView
                  and roleView entries below. The permission shipped with
                  the AddProposalPermissions migration and was enforced
                  nowhere, so a role with every proposal box unticked
                  still saw this link and could read the whole surface. */}
              {(user?.role?.permissions || []).find((x: any) => ["proposalView"].includes(x)) &&
                <LinkWrapper>
                  <NavLink link={"/proposals"} icon={ProposalsIcon} label={"Proposals"}></NavLink>
                </LinkWrapper>
              }

              <NavSubtitle $mt={"20px"}>SETTINGS</NavSubtitle>           
              {(user?.role?.permissions || []).find((x: any) => ["userView"].includes(x)) &&
                <LinkWrapper>
                  <NavLink link={"/users"} icon={UsersIcon} label={"Users"}></NavLink>
                </LinkWrapper>
              }   
              {(user?.role?.permissions || []).find((x: any) => ["roleView"].includes(x)) &&
                <LinkWrapper>
                  <NavLink link={"/roles"} icon={RolesIcon} label={"Roles"}></NavLink>
                </LinkWrapper>
              }  
              {(user?.role?.permissions || []).find((x: any) => ["roleView"].includes(x)) &&
                <LinkWrapper>
                  <NavLink link={"/company"} icon={CompanyIcon} label={"Company"}></NavLink>
                </LinkWrapper>
              }
              <LinkWrapper>
                <NavLink link={"/settings"} icon={SettingsIcon} label={"My Settings"}></NavLink>
              </LinkWrapper>
              <div style={{ marginTop: "auto", paddingBottom: "30px" }}>
                <LinkWrapper>
                  <NavLink link={"/ai-chat"} icon={ChatIcon} label={"AI Chat"}/>
                </LinkWrapper>
                <LinkWrapper>
                  <NavLink link="https://getmulch.atlassian.net/servicedesk/customer/portals" icon={SupportIcon} label={"Support"} target={'_blank'}/>
                </LinkWrapper>
              </div>                      
            </NavInnerMenu>
          </>}
          {["isClient","isClientAdmin"].includes(user?.accessLevel) && <>
            <NavInnerMenu> 
              <NavSubtitle>GENERAL</NavSubtitle>
               <LinkWrapper>
                <NavLink link={"/dashboard"} icon={DashboardIcon} label={"Dashboard"}></NavLink>
              </LinkWrapper>  
              <LinkWrapper>
                <NavLink link={"/quote-requests"} icon={RFQIcon} label={"RFQ's"} badge={expiringRfqCount}></NavLink>
              </LinkWrapper>    
              <LinkWrapper>
                <NavLink link={"/quotes"} icon={QuotesIcon} label={"Quotes"} badge={expiringQuoteCount}></NavLink>
              </LinkWrapper>
              <LinkWrapper>
                <NavLink link={"/invoices"} icon={InvoicesIcon} label={"Invoices"}></NavLink>
              </LinkWrapper>   
              <LinkWrapper>
                <NavLink link={"/projects"} icon={ProjectsIcon} label={"Projects"}></NavLink>
              </LinkWrapper>              
              {/* Session 62 - client Maintenance. Same route as staff
                  (/maintenance), gated by role in the components:
                  /maintenance and /maintenance/plans/:id render a lean
                  read-only client view, and /maintenance/work-orders/:id
                  renders the client job status. A single link (not the
                  staff NavLinkGroup with a Work Orders queue child): a
                  client reaches a work order by clicking a site in their
                  plan, not through the operator triage queue.
                  Session 66 - shown ONLY when the client actually has a
                  maintenance plan. PERFORMANCE_COST_AUDIT finding #1 -
                  hasMaintenancePlan now comes from the session user
                  payload (stamped by the auth path), not a per-load
                  plan-list fetch. A client with no plan no longer sees an
                  empty Maintenance page in the nav. */}
              {hasMaintenancePlan && (
                <LinkWrapper>
                  <NavLink link={"/maintenance"} icon={RFQIcon} label={"Maintenance"}></NavLink>
                </LinkWrapper>
              )}
              {/* N1 Release 1 - client portal Locations. Gated on the
                  client org's portalLocationsEnabled flag, resolved
                  parent-aware above (the flag lives on the type-2
                  organization, not the individual user's client record). */}
              {portalLocationsEnabled && (
                <LinkWrapper>
                  <NavLink link={"/portal/locations"} icon={LocationsIcon} label={"Locations"}></NavLink>
                </LinkWrapper>
              )}
              {/* N1 - client TOOLS section. The single "Mulch Calculator"
                  control on the client edit page enables both portal
                  surfaces, so the calculator link is gated on the same
                  flag as Locations. Mirrors the company TOOLS section. */}
              {/* Per-person too (2026-10-09): the org has it AND this user may use it. */}
              {portalLocationsEnabled && personHasCalculator(user) && (
                <>
                  <NavSubtitle $mt={"20px"}>TOOLS</NavSubtitle>
                  <LinkWrapper>
                    <NavLink link={"/mulch-calculator"} icon={CalculatorIcon} label={"Mulch Calculator"}></NavLink>
                  </LinkWrapper>
                </>
              )}
              {user?.accessLevel === "isClientAdmin" && <>
                <NavSubtitle $mt={"20px"}>SETTINGS</NavSubtitle>
                <LinkWrapper>
                  <NavLink link={"/client-company"} icon={CompanyIcon} label={"Company"}></NavLink>
                </LinkWrapper>
                <LinkWrapper>
                  <NavLink link={"/settings"} icon={SettingsIcon} label={"My Settings"}></NavLink>
                </LinkWrapper>
                {/* Users (2026-10-09): only for orgs staff have switched on
                    ("Client admins manage their own users" on the client edit
                    page), once that org's admin is confirmed. Groups / Regions
                    stay hidden. */}
                {orgManagesUsers(user) && (
                  <LinkWrapper>
                    <NavLink link={"/clients/users"} icon={UsersIcon} label={"Users"}></NavLink>
                  </LinkWrapper>
                )}
                {/* <LinkWrapper>
                  <NavLink link={"/clients/groups"} icon={GroupsIcon} label={"Groups"}></NavLink>
                </LinkWrapper>
                <LinkWrapper>
                  <NavLink link={"/clients/regions"} icon={RegionsIcon} label={"Regions"}></NavLink>
                </LinkWrapper> */}
              </>}
              {user?.accessLevel === "isClient" && <>
                <NavSubtitle $mt={"20px"}>SETTINGS</NavSubtitle>
                <LinkWrapper>
                  <NavLink link={"/settings"} icon={SettingsIcon} label={"My Settings"}></NavLink>
                </LinkWrapper>
              </>}
              <div style={{ marginTop: "auto", paddingBottom: "30px" }}>
                <LinkWrapper>
                  <NavLink link="https://getmulch.atlassian.net/servicedesk/customer/portals" icon={SupportIcon} label={"Support"} target={'_blank'}/>
                </LinkWrapper>
              </div>
            </NavInnerMenu>
          </>}
          {["isProvider"].includes(user?.accessLevel) && <>
            <NavInnerMenu> 
              <NavSubtitle>GENERAL</NavSubtitle>
              <LinkWrapper>
                <NavLink link={"/dashboard"} icon={DashboardIcon} label={"Dashboard"}></NavLink>
              </LinkWrapper>
              <LinkWrapper>
                 <NavLink link={"/purchase-orders"} icon={PurchaseOrderIcon} label={"PO's"} badge={pendingInvoiceCount}></NavLink>
              </LinkWrapper>   
               <LinkWrapper>
                <NavLink link={"/projects"} icon={ProjectsIcon} label={"Projects"}></NavLink>
              </LinkWrapper>            
              <div style={{ marginTop: "auto", paddingBottom: "30px" }}>
                <LinkWrapper>
                  <NavLink link="https://getmulch.atlassian.net/servicedesk/customer/portals" icon={SupportIcon} label={"Support"} target={'_blank'}/>
                </LinkWrapper>
              </div>
            </NavInnerMenu>
          </>}
        </>          
        }
      </NavInner>
    </NavWrapper>
  );
};
export default NavbarLayout;
